import { Fragment, isValidElement, useState } from "react";
import { Link } from "react-router-dom";
import { capture } from "../../instrument";
import { HiOutlineChevronDown, HiOutlineDocumentText, HiOutlineExternalLink } from "react-icons/hi";
import {
  AUDIT_COLLECTION_LABELS,
  EFFECT_TYPES,
  EMAIL_TYPE_LABELS,
  EXAM_TYPE_LABEL,
  NON_COMMUNIQUE,
  SEXE_LABEL,
  STATUS_CONFIG,
  STATUS_REASON_LABELS,
  USER_ROLE_LABEL,
  formatActorName,
} from "../../utils";
import { ACTION_PHRASES, FLAG_LABELS, PATH_LABELS, PROCESSING_STEP_LABELS, describeTriggerGroup } from "./labels";

const reportedMissingLabels = new Set();

const reportMissingLabel = (label) => {
  if (reportedMissingLabels.has(label)) return;
  reportedMissingLabels.add(label);
  capture(new Error(`Audit label missing: ${label}`));
};

const STATUS_PHRASES = {
  DRAFT: "a repassé le dossier en brouillon",
  TO_VALIDATE: "a remis le dossier à valider",
  ON_HOLD: "a mis le dossier en attente",
  VALIDATED: "a validé le dossier",
  FINALIZING: "a lancé la finalisation du dossier",
  ERROR: "a rencontré une erreur d'enregistrement",
  QUEUED: "a envoyé le dossier en production",
  RECORDED: "a enregistré le dossier dans le SIL",
  ARCHIVED: "a archivé le dossier",
};

const ENTITY_PATH = {
  admissions: (event) => event.documentId && `/admissions/${event.documentId}`,
  prescriptions: (event) => (event.admissionId ? `/admissions/${event.admissionId}` : event.documentId && `/prescription/${event.documentId}`),
  questionrecords: (event) => event.admissionId && `/admissions/${event.admissionId}`,
  questions: (event) => event.documentId && `/question/${event.documentId}/edit`,
  guidelines: (event) => event.documentId && `/guideline/${event.documentId}/edit`,
  code2: (event) => event.documentId && `/code/${event.documentId}/edit`,
  table2: (event) => event.documentId && `/table/${event.documentId}/edit`,
  actions: (event) => event.documentId && `/action/${event.documentId}/edit`,
  laboratories: (event) => event.documentId && `/laboratory/${event.documentId}/edit`,
  clinics: (event) => event.documentId && `/clinic/${event.documentId}/edit`,
  networks: (event) => event.documentId && `/network/${event.documentId}/edit`,
};

const humanize = (path) => {
  const segment = path.split(".").pop();
  const words = (segment === segment.toUpperCase() ? segment : segment.replace(/([A-Z])/g, " $1")).replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const labelOf = (path) => {
  const segments = path.split(".");
  const label = PATH_LABELS[path] || PATH_LABELS[segments.slice(-2).join(".")] || PATH_LABELS[segments[segments.length - 1]];
  if (label) return label;
  reportMissingLabel(`path ${path}`);
  return humanize(path);
};

const isBlank = (value) => value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);
const isDocumentKey = (value) => typeof value === "string" && value.startsWith("admissions/");
const isObjectIdString = (value) => /^[0-9a-f]{24}$/.test(value);
const prescriptionLabel = (label) => (isObjectIdString(label) ? "Ordonnance" : label);

const changeLabel = (change) => {
  const subject = change.subject?.replace(/\s*:\s*$/, "");
  if (subject && /(^|\.)(answer|validated)$/.test(change.path)) return subject;
  return subject ? `${labelOf(change.path)} · ${subject}` : labelOf(change.path);
};

const PROCESSING_RESULTS = {
  SUCCESS: { label: "Succès", className: "text-emerald-700" },
  ERROR: { label: "Erreur", className: "text-red-600" },
  PARTIAL: { label: "Partiel · à terminer manuellement", className: "text-amber-700" },
};

const machineLogsUrl = (event) => {
  const eventTime = new Date(event.createdAt).getTime();
  const startDate = new Date(eventTime - 60 * 60 * 1000).toISOString();
  const endDate = new Date(eventTime + 60 * 60 * 1000).toISOString();
  const { streamName, logGroupName } = event.data.machine;
  return `/machine/${encodeURIComponent(streamName)}/logs?logGroupName=${encodeURIComponent(logGroupName)}&admissionId=${encodeURIComponent(event.documentId)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
};

const onOpenWindow = (clickEvent, url, name) => {
  clickEvent.preventDefault();
  clickEvent.stopPropagation();
  window.open(url, name, "noopener,noreferrer,width=1000,height=800,scrollbars=yes");
};

const formatDuration = (ms) => {
  const seconds = Math.round(ms / 1000);
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, "0")} s`;
};

const processingEntries = (event) => {
  const { status, steps = [], silAdmissionNumber = event.data.externalId, duration, error, leaseLost, screenshotUrl, machine } = event.data;
  const lastStep = steps[steps.length - 1];
  const result = PROCESSING_RESULTS[status];
  return [
    [
      "result",
      <span key="result" className={result?.className || "text-gray-900"}>
        {result?.label || "Interrompu"}
      </span>,
    ],
    leaseLost && ["leaseLost", "Résultat non pris en compte"],
    silAdmissionNumber && ["silAdmissionNumber", silAdmissionNumber],
    duration > 0 && ["duration", formatDuration(duration)],
    error?.message && ["error", [error.name, error.message].filter(Boolean).join(" · ")],
    steps.length > 0 && [
      "steps",
      <span key="steps" className="grid grid-cols-[auto_1fr] gap-x-2 font-normal text-gray-600">
        {steps.map((step, index) => (
          <Fragment key={`${step}-${index}`}>
            <span className="text-right tabular-nums text-gray-400">{index + 1}.</span>
            <span className={status === "ERROR" && step === lastStep ? "font-medium text-red-600" : undefined}>{PROCESSING_STEP_LABELS[step] || step}</span>
          </Fragment>
        ))}
      </span>,
    ],
    machine?.streamName && [
      "machine",
      machine.logGroupName ? (
        <a
          key="machine"
          href={machineLogsUrl(event)}
          onClick={(e) => onOpenWindow(e, machineLogsUrl(event), "admission-machine-logs")}
          className="inline-flex items-center gap-1 hover:text-gray-600"
        >
          {machine.streamName}
          <HiOutlineExternalLink className="h-3.5 w-3.5 text-gray-400" />
        </a>
      ) : (
        machine.streamName
      ),
    ],
    screenshotUrl && [
      "screenshot",
      <a
        key="screenshot"
        href={screenshotUrl}
        onClick={(e) => onOpenWindow(e, screenshotUrl, "admission-screenshot")}
        className="inline-flex items-center gap-1 hover:text-gray-600"
      >
        Ouvrir
        <HiOutlineExternalLink className="h-3.5 w-3.5 text-gray-400" />
      </a>,
    ],
  ].filter(Boolean);
};

const attachmentsOf = (change) => {
  const attachments =
    change.path === "prescriptions"
      ? [
          ...change.from.map((label, index) => ({ url: change.fromUrls?.[index], title: prescriptionLabel(label), state: "Retirée du dossier", isRemoved: true })),
          ...change.to.map((label, index) => ({ url: change.toUrls?.[index], title: prescriptionLabel(label), state: "Ajoutée au dossier" })),
        ]
      : [
          isDocumentKey(change.from) && { url: change.fromUrl, title: "Document", state: isBlank(change.to) ? "Supprimé" : "Remplacé", isRemoved: true },
          isDocumentKey(change.to) && { url: change.toUrl, title: "Document", state: isBlank(change.from) ? "Ajouté" : "Nouveau" },
        ].filter(Boolean);
  return attachments.every((attachment) => attachment.url) ? attachments : [];
};

const formatValue = (path, value) => {
  if (isValidElement(value)) return value;
  const triggerGroup = path === "triggerGroups" ? describeTriggerGroup(value) : null;
  if (triggerGroup) return triggerGroup;
  // Avant l'enregistrement des groupes entiers, seul un marqueur de position était stocké.
  if (path === "triggerGroups") return <span className="italic text-gray-400">un groupe de conditions (détail non enregistré)</span>;
  if (isBlank(value)) return <span className="italic text-gray-400">vide</span>;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (path === "EXAM_TYPE_PRIORITY") return EXAM_TYPE_LABEL[value] || value;
  if (path === "field") return PATH_LABELS[value] || value;
  if (path === "flags") return FLAG_LABELS[value] || value;
  if (path === "mode") return value === "once" ? "Une seule fois par dossier" : "À chaque fois que les conditions deviennent vraies";
  if (path.endsWith("effects.type")) return EFFECT_TYPES.find((effect) => effect.value === value)?.label || value;
  if (path === "groupsOperator" || path.endsWith(".operator")) return value === "OR" ? "OU" : "ET";
  if (path === "type" && EXAM_TYPE_LABEL[value]) return EXAM_TYPE_LABEL[value];
  if (path === "status") {
    return (
      <span className="status-pill" data-status={value}>
        {STATUS_CONFIG[value]?.label || value}
      </span>
    );
  }
  if (path === "statusReason") return STATUS_REASON_LABELS[value] || value;
  if (path === "role") return USER_ROLE_LABEL[value] || value;
  if (path === "type" && EMAIL_TYPE_LABELS[value]) return EMAIL_TYPE_LABELS[value];
  if (path.endsWith("sexe")) return SEXE_LABEL[value] || value;
  if (value === NON_COMMUNIQUE) return "Non communiqué";
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      return new Date(value).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(value).toLocaleDateString("fr-FR");
    if (isDocumentKey(value)) return "Document";
    if (path === "prescriptions") return prescriptionLabel(value);
    if (isObjectIdString(value)) return <span className="font-mono text-[11px] text-gray-500">#{value.slice(-6)}</span>;
    return value;
  }
  if (typeof value === "object") return <span className="break-all font-mono text-[11px] text-gray-500">{JSON.stringify(value)}</span>;
  return String(value);
};

const SUBJECT_ARTICLES = {
  Question: "la question",
  Table: "la table",
  Code: "le code",
  Examen: "l'examen",
  Recommandation: "la recommandation",
  Action: "l'action",
  Laboratoire: "le laboratoire",
  Clinique: "la clinique",
  Réseau: "le réseau",
  Utilisateur: "l'utilisateur",
  IDE: "l'IDE",
  Patient: "le patient",
};

const withArticle = (subject) => {
  if (!subject) return "";
  const [, kind, text] = /^(\S+) · (.+)$/.exec(subject) || [];
  if (kind && SUBJECT_ARTICLES[kind]) return `${SUBJECT_ARTICLES[kind]} « ${text} »`;
  const lower = subject.charAt(0).toLowerCase() + subject.slice(1);
  return /^[aeiouyéèêàâîôû]/i.test(lower) ? `l'${lower}` : `« ${subject} »`;
};

const listPhrase = (items) => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`);

const describeUpdate = (event) => {
  const changes = event.changes || [];
  const byPath = (suffix) => changes.filter((change) => change.path === suffix || change.path.endsWith(`.${suffix}`));
  const parts = [];
  const used = new Set();
  const say = (text, anchor) => parts.push({ text, index: anchor ? changes.indexOf(anchor) : Infinity });
  const where = event.collectionName === "prescriptions" && event.subject ? ` sur ${withArticle(event.subject)}` : "";

  const deletedDocuments = byPath("answer").filter((change) => isDocumentKey(change.from) && isBlank(change.to));
  deletedDocuments.forEach((change) => used.add(change));
  if (deletedDocuments.length === 1) say(deletedDocuments[0].subject ? `a supprimé le document « ${deletedDocuments[0].subject} »` : "a supprimé un document", deletedDocuments[0]);
  if (deletedDocuments.length > 1) say(`a supprimé ${deletedDocuments.length} documents`, deletedDocuments[0]);

  const answers = byPath("answer").filter((change) => !deletedDocuments.includes(change));
  answers.forEach((change) => used.add(change));
  if (answers.length === 1) {
    const questionName = answers[0].subject || (event.collectionName === "questionrecords" ? event.subject?.replace(/^Question · /, "") : undefined);
    say(questionName ? `a répondu à « ${questionName} »` : "a répondu à une question", answers[0]);
  }
  if (answers.length > 1) say(`a répondu à ${answers.length} questions`, answers[0]);

  const status = byPath("status")[0];
  if (status) {
    used.add(status);
    const reason = byPath("statusReason")[0];
    const rejectReason = byPath("rejectReason")[0];
    const submittedAt = byPath("submittedAt")[0];
    const silNumber = byPath("patient.admissionId")[0];
    [reason, rejectReason, submittedAt].filter(Boolean).forEach((change) => used.add(change));
    if (status.to === "TO_VALIDATE" && status.from === "DRAFT") say("a soumis la demande", status);
    else if (status.to === "ARCHIVED" && reason?.to === "REJECTED") say(`a refusé le dossier${rejectReason?.to ? ` (${rejectReason.to})` : ""}`, status);
    else if (status.to === "ARCHIVED" && reason?.to === "ABANDONED") say("a abandonné la demande", status);
    else if (status.to === "ARCHIVED" && reason?.to === "MERGED") say("a fusionné le dossier", status);
    else if (status.to === "RECORDED" && reason?.to === "PARTIAL") say("a enregistré partiellement le dossier dans le SIL (à terminer manuellement)", status);
    else if (status.to === "RECORDED" && silNumber?.to) {
      used.add(silNumber);
      say(`a enregistré le dossier dans le SIL sous le n° ${silNumber.to}`, status);
    } else say(STATUS_PHRASES[status.to] || `a passé le dossier en ${STATUS_CONFIG[status.to]?.label || status.to}`, status);
  }

  const acts = byPath("acts")[0];
  if (acts) {
    used.add(acts);
    const removed = acts.from || [];
    const added = acts.to || [];
    if (removed.length === 1 && added.length === 1) say(`a remplacé ${removed[0]} par ${added[0]}${where}`, acts);
    else if (removed.length === 0) say(`a ajouté ${listPhrase(added)}${where}`, acts);
    else if (added.length === 0) say(`a retiré ${listPhrase(removed)}${where}`, acts);
    else say(`a modifié les analyses${where} (− ${removed.join(", ")}, + ${added.join(", ")})`, acts);
  }

  const checkIn = byPath("checkInDate")[0];
  if (checkIn) {
    used.add(checkIn);
    say(event.actor.kind === "patient" ? "s'est présenté au laboratoire" : "a enregistré l'arrivée du patient", checkIn);
  }

  const validatedGuidelines = byPath("validated");
  validatedGuidelines.forEach((change) => used.add(change));
  if (validatedGuidelines.length > 0) {
    say(
      validatedGuidelines.length === 1 ? `a validé la recommandation « ${validatedGuidelines[0].subject} »` : `a validé ${validatedGuidelines.length} recommandations`,
      validatedGuidelines[0],
    );
  }

  const forced = byPath("forced")[0];
  if (forced) {
    used.add(forced);
    say(forced.to ? "a forcé la question" : "a retiré le forçage de la question", forced);
  }

  const removal = byPath("deleted")[0] || byPath("isDeleted")[0];
  if (removal) {
    used.add(removal);
    say(removal.to ? `a supprimé ${withArticle(event.subject)}` : `a restauré ${withArticle(event.subject)}`, removal);
  }

  const shared = byPath("sharedToNetwork")[0];
  if (shared) {
    used.add(shared);
    say(shared.to ? `a partagé ${withArticle(event.subject)} avec le réseau` : `a retiré ${withArticle(event.subject)} du partage réseau`, shared);
  }

  const questionMembership = changes.find((change) => change.path === "questionRecords");
  if (questionMembership) {
    used.add(questionMembership);
    const added = questionMembership.to || [];
    const removed = questionMembership.from || [];
    if (added.length) say(added.length === 1 ? `a ajouté la question « ${added[0]} »` : `a ajouté ${added.length} questions`, questionMembership);
    if (removed.length) say(removed.length === 1 ? `a retiré la question « ${removed[0]} »` : `a retiré ${removed.length} questions`, questionMembership);
  }

  for (const prescriptionMembership of changes.filter((change) => change.path === "prescriptions")) {
    used.add(prescriptionMembership);
    if (prescriptionMembership.to?.length) say(`a ajouté ${listPhrase(prescriptionMembership.to.map((label) => withArticle(prescriptionLabel(label))))}`, prescriptionMembership);
    if (prescriptionMembership.from?.length) say(`a retiré ${listPhrase(prescriptionMembership.from.map(withArticle))}`, prescriptionMembership);
  }

  const others = changes.filter((change) => !used.has(change));
  if (others.length > 0 && others.length <= 3) say(`a modifié ${listPhrase(others.map((change) => labelOf(change.path)))}${acts ? "" : where}`);
  if (others.length > 3) say(`a modifié ${others.length} champs${acts ? "" : where}`);

  const sentence = listPhrase(parts.sort((a, b) => a.index - b.index).map((part) => part.text));
  return sentence || `a modifié ${event.collectionName === "admissions" ? "le dossier" : withArticle(event.subject)}`;
};

const describeEvent = (event) => {
  if (event.action === "UPDATE") return describeUpdate(event);
  if (event.action === "CREATE") {
    if (event.collectionName === "admissions") return event.actor.kind === "patient" ? "a créé le dossier depuis l'application" : "a créé le dossier";
    if (event.collectionName === "prescriptions") return `a ajouté ${withArticle(event.subject || "Ordonnance")}`;
    if (event.collectionName === "questionrecords") return `a ajouté la question « ${event.subject?.replace(/^Question · /, "")} »`;
    if (event.collectionName === "ignoredterms") return `a ajouté « ${event.subject?.replace(/^Terme ignoré · /, "")} » aux termes ignorés`;
    return `a créé ${withArticle(event.subject || AUDIT_COLLECTION_LABELS[event.collectionName])}`;
  }
  if (event.action === "DELETE" && event.collectionName === "ignoredterms") return `a retiré « ${event.subject?.replace(/^Terme ignoré · /, "")} » des termes ignorés`;
  if (event.action === "DELETE") return `a supprimé ${withArticle(event.subject || AUDIT_COLLECTION_LABELS[event.collectionName])}`;
  if (event.action === "EMAIL_SENT") return `a envoyé l'email « ${EMAIL_TYPE_LABELS[event.data?.type] || event.data?.type || "personnalisé"} »`;
  if (event.action === "PROCESSING") {
    const { status, silAdmissionNumber = event.data?.externalId, leaseLost } = event.data || {};
    const silNumber = silAdmissionNumber ? ` (n° ${silAdmissionNumber})` : "";
    const afterLeaseLost = leaseLost ? " après la perte de son verrou" : "";
    if (status === "SUCCESS") return `a terminé le traitement du dossier dans le SIL${silNumber}${afterLeaseLost}`;
    if (status === "PARTIAL") return `a interrompu le traitement après la création du dossier dans le SIL${silNumber}${afterLeaseLost}`;
    if (status === "ERROR") return `a échoué à traiter le dossier dans le SIL${afterLeaseLost}`;
    return ACTION_PHRASES.PROCESSING;
  }
  if (event.action === "ACTIONS_APPLIED") {
    const actionNames = [...new Set(event.changes.map((change) => change.subject))];
    return actionNames.length === 1 ? `a appliqué l'action « ${actionNames[0]} »` : `a appliqué ${actionNames.length} actions sur les analyses`;
  }
  if (ACTION_PHRASES[event.action]) return ACTION_PHRASES[event.action];
  reportMissingLabel(`action ${event.action}`);
  return event.action.toLowerCase().replace(/_/g, " ");
};

const AuditEventCard = ({ event, showSubject }) => {
  const [expanded, setExpanded] = useState(false);
  const [showSaves, setShowSaves] = useState(false);
  const changes = event.changes || [];
  const isProcessingRun = event.action === "PROCESSING";
  const dataEntries = isProcessingRun
    ? processingEntries(event)
    : Object.entries(event.data && typeof event.data === "object" && !Array.isArray(event.data) ? event.data : {}).filter(
        ([, value]) => typeof value !== "object" || value === null,
      );
  const hasDetails = changes.length > 0 || dataEntries.length > 0;
  const createdAt = new Date(event.createdAt);
  const entityPath = ENTITY_PATH[event.collectionName]?.(event);
  if (!AUDIT_COLLECTION_LABELS[event.collectionName]) reportMissingLabel(`collection ${event.collectionName}`);
  const subject = event.subject || AUDIT_COLLECTION_LABELS[event.collectionName] || event.collectionName;

  return (
    <div className={`rounded-lg border bg-white px-4 py-3 transition-colors ${expanded ? "border-primary-200" : "border-gray-100"}`}>
      <div className={`flex items-start gap-3.5 ${hasDetails ? "cursor-pointer hover:border-gray-200" : ""}`} onClick={() => hasDetails && setExpanded((current) => !current)}>
        <span className="w-11 shrink-0 pt-0.5 text-[12px] tabular-nums text-gray-400" title={createdAt.toLocaleString("fr-FR")}>
          {createdAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
        </span>
        <div className="min-w-0 flex-1 text-[14px] leading-5 text-gray-900">
          <span className="font-semibold" title={event.actor.label}>
            {formatActorName(event.actor)}
          </span>
          {event.actor.kind === "clinician" && <span className="meta-badge ml-1.5 bg-gray-100 text-gray-500">IDE</span>}
          <span> {describeEvent(event)}.</span>
          {event.saves && <span className="meta-badge ml-1.5 bg-gray-100 text-gray-500">{event.saves.length} enregistrements</span>}
        </div>
        {showSubject &&
          (entityPath ? (
            <Link
              to={entityPath}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[260px] shrink-0 truncate pt-0.5 text-[13px] text-gray-500 transition-colors hover:text-primary-600 hover:underline"
            >
              {subject}
            </Link>
          ) : (
            <span className="max-w-[260px] shrink-0 truncate pt-0.5 text-[13px] text-gray-500">{subject}</span>
          ))}
        {hasDetails && <HiOutlineChevronDown className={`mt-1 h-4 w-4 shrink-0 text-gray-300 transition-transform ${expanded ? "rotate-180" : ""}`} />}
      </div>

      {expanded && (
        <div className="ml-[58px] mt-3 flex flex-col gap-0.5 border-t border-gray-100 pt-2.5 text-[13px] leading-5">
          {changes.map((change, index) => {
            const isSet = Array.isArray(change.from) || Array.isArray(change.to);
            const hasFrom = !isBlank(change.from);
            const attachments = attachmentsOf(change);
            return (
              <div
                key={`${change.path}-${index}`}
                className={`-mx-2 grid grid-cols-[minmax(0,200px)_1fr] gap-x-6 rounded px-2 hover:bg-gray-50 ${attachments.length > 0 ? "items-center py-1" : "py-0.5"}`}
              >
                <span className="min-w-0 text-gray-500" title={change.path}>
                  {changeLabel(change)}
                </span>
                <span className={`flex min-w-0 gap-x-2 gap-y-0.5 break-words ${change.path === "triggerGroups" ? "flex-col items-start" : "flex-wrap items-center"}`}>
                  {attachments.map((attachment) => (
                    <a
                      key={attachment.url}
                      href={attachment.url}
                      title="Ouvrir"
                      onClick={(e) => onOpenWindow(e, attachment.url, "audit-document")}
                      className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-1 pr-4 transition hover:border-gray-300 hover:shadow-sm"
                    >
                      {/\.pdf(\?|$)/i.test(attachment.url) ? (
                        <span className="flex h-12 w-9 items-center justify-center rounded-md bg-gray-50 text-gray-400">
                          <HiOutlineDocumentText className="h-5 w-5" />
                        </span>
                      ) : (
                        <img
                          src={attachment.url}
                          alt=""
                          loading="lazy"
                          className={`h-12 w-9 rounded-md bg-gray-50 object-cover transition ${attachment.isRemoved ? "opacity-60 grayscale group-hover:opacity-100 group-hover:grayscale-0" : ""}`}
                        />
                      )}
                      <span className="flex flex-col leading-4">
                        <span className={`font-medium ${attachment.isRemoved ? "text-gray-500" : "text-gray-900"}`}>{attachment.title}</span>
                        <span className="text-[12px] text-gray-400">{attachment.state}</span>
                      </span>
                    </a>
                  ))}
                  {attachments.length === 0 && isSet && (
                    <>
                      {(change.from || []).map((value, valueIndex) => (
                        <span key={`-${valueIndex}`} className="text-gray-400">
                          − {formatValue(change.path, value)}
                        </span>
                      ))}
                      {(change.to || []).map((value, valueIndex) => (
                        <span key={`+${valueIndex}`} className="font-medium text-gray-900">
                          + {formatValue(change.path, value)}
                        </span>
                      ))}
                    </>
                  )}
                  {attachments.length === 0 && !isSet && (
                    <>
                      {hasFrom && <span className="text-gray-400">{change.fromLabel || formatValue(change.path, change.from)}</span>}
                      {hasFrom && <span className="text-gray-300">→</span>}
                      <span className="font-medium text-gray-900">{change.toLabel || formatValue(change.path, change.to)}</span>
                    </>
                  )}
                </span>
              </div>
            );
          })}
          {dataEntries.map(([key, value]) => (
            <div key={key} className="-mx-2 grid grid-cols-[minmax(0,200px)_1fr] gap-x-6 rounded px-2 py-0.5 hover:bg-gray-50">
              <span className="min-w-0 text-gray-500">{labelOf(key)}</span>
              <span className="min-w-0 break-words font-medium text-gray-900">{formatValue(key, value)}</span>
            </div>
          ))}
        </div>
      )}
      {expanded && event.saves && (
        <div className="ml-[58px] mt-2 text-[12px]" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => setShowSaves((current) => !current)} className="text-gray-400 hover:text-gray-600 hover:underline">
            {showSaves ? "Masquer les enregistrements" : `Voir les ${event.saves.length} enregistrements`}
          </button>
          {showSaves && (
            <div className="mt-1.5 flex flex-col gap-0.5">
              {event.saves.map((save) => (
                <div key={save._id} className="-mx-2 flex items-start gap-3 rounded px-2 py-0.5 hover:bg-gray-50">
                  <span className="w-14 shrink-0 tabular-nums text-gray-400">{new Date(save.createdAt).toLocaleTimeString("fr-FR")}</span>
                  <span className="min-w-0 flex-1 break-words text-gray-600">
                    {(save.changes || []).map((change, index) => (
                      <span key={`${change.path}-${index}`} className="mr-3">
                        {changeLabel(change)} :{" "}
                        {Array.isArray(change.from) || Array.isArray(change.to) ? (
                          [...(change.from || []).map((value) => ["−", value]), ...(change.to || []).map((value) => ["+", value])].map(([sign, value], valueIndex) => (
                            <span key={`${sign}${valueIndex}`} className="mr-1.5">
                              {sign} {formatValue(change.path, value)}
                            </span>
                          ))
                        ) : (
                          <>
                            {change.fromLabel || formatValue(change.path, change.from)} → {change.toLabel || formatValue(change.path, change.to)}
                          </>
                        )}
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditEventCard;
